<template lang="pug">
    .establishments-categories
        v-dialog(
            v-model="removeDialog"
            persistent
            width="400px"
        )
            v-card
                v-card-title
                    span.title.mx-auto {{$vuetify.lang.t('$vuetify.pages.EstablishmentsTypes.deletingType')}}
                v-card-text.pb-1.text-center
                    span {{$vuetify.lang.t('$vuetify.pages.EstablishmentsTypes.areYouSureToDelete')}}
                v-card-actions.d-flex.justify-center
                    v-btn(@click="closeRemoveDialog" text) {{$vuetify.lang.t('$vuetify.buttons.cancel')}}
                    v-btn.white--text(@click="remove" color="red") {{$vuetify.lang.t('$vuetify.buttons.remove')}}
        v-dialog(
            v-model="editDialog"
            v-if="editDialog"
            width="800px"
            persistent
        )
            v-card.px-3
                v-card-title.mb-7
                    span.headline.mx-auto {{ $vuetify.lang.t(`$vuetify.pages.EstablishmentsTypes.table.${isEditMode ? 'edit' : 'create'}`) }}
                v-card-text.py-0
                    v-locale(controlled v-model="editingLocale")
                    v-row.mb-4.mt-3
                        v-col.py-0(cols="12" sm="6")
                            v-text-field(
                                v-model="type[localizeField('name', editingLocale)]"
                                :label="$vuetify.lang.t('$vuetify.pages.EstablishmentsTypes.table.name') + ' *'"
                                outlined
                                :counter="255"
                                maxLength="255"
                            )
                        v-col.py-0(cols="12" sm="6")
                            v-select(
                                :items="statuses"
                                :disabled="!isEditMode"
                                :label="$vuetify.lang.t('$vuetify.pages.EstablishmentsTypes.table.status') + ' *'"
                                item-value="key"
                                item-text="text"
                                v-model="type.status"
                                color="secondary"
                                hide-details
                                outlined
                            )
                    v-row
                        v-col.py-0(cols="12" sm="12")
                            v-text-field(
                                v-model="type[localizeField('description', editingLocale)]"
                                :counter="255"
                                maxLength="255"
                                :label="$vuetify.lang.t('$vuetify.pages.EstablishmentsTypes.table.description')"
                                outlined
                            )
                    v-image-uploader.mt-8(
                        :width="280"
                        :height="210"
                        :initial-image="type.imgUrl"
                        :input-label="$vuetify.lang.t('$vuetify.pages.Delivery.photo')",
                        @choose-image="onChooseImage"
                    )
                v-card-actions.d-flex.justify-end.buttons
                    v-btn.white--text(@click="closeEditDialog" color="red") {{$vuetify.lang.t('$vuetify.buttons.cancel')}}
                    v-btn(
                        :loading="requestProcessing"
                        :disabled="!isSaveBtnEnabled"
                        @click="saveType"
                        color="secondary"
                    ) {{$vuetify.lang.t(`$vuetify.buttons.${isEditMode ? 'save' : 'create'}`)}}
        .v-card.elevation-5.pa-4.mb-5
            v-card-text.d-flex.justify-center
                span.headline {{ $vuetify.lang.t('$vuetify.filters') }}
            v-card-text.px-0
                v-row.d-flex.justify-center
                    v-col.py-0(cols="12" sm="12")
                        v-text-field(
                            :counter="255"
                            :label="$vuetify.lang.t('$vuetify.pages.Brands.filters.query')"
                            v-model="filters.query"
                            @blur="loadItems()"
                            outlined
                            clearable
                        )
        .table-wrapper
            v-data-table.elevation-5.data-table(
                :headers="headers"
                :items="items"
                :items-per-page="200"
                :mobile-breakpoint="300"
                hide-default-footer
            )
                template(v-slot:item.name="{ item }")
                    span {{ item.name }}
                template(v-slot:item.description="{ item }")
                    span {{ item.description }}
                template(v-slot:item.status="{ item }")
                    span {{ $vuetify.lang.t(`$vuetify.pages.EstablishmentsTypes.table.statuses.${item.status}`) }}
                template(v-slot:item.createdAt="{ item }")
                    span.no-wrap {{ item.createdAt | timeAndDate }}
                template(v-slot:item.updatedAt="{ item }")
                    span.no-wrap {{item.updatedAt | timeAndDate }}
                template(v-slot:item.actions="{ item }")
                    v-btn(@click="openEditDialog(item)" icon)
                        v-icon edit
                    v-btn(@click="openRemoveDialog(item.id)" color="red" icon :disabled="!!Number(item.establishmentCnt)")
                        v-icon delete
        v-pagination(
            v-if="pagination.totalPages > 1"
            :disabled="requestProcessing"
            :value="pagination.page"
            :length="pagination.totalPages"
            @input="onPageChange($event)"
            :total-visible="8"
            color="black"
        )
        v-btn.mx-2.add-btn(
            @click="openEditDialog"
            fab dark
        )
            v-icon(dark) mdi-plus
</template>

<script>
import request from '@/utils/request';
import { getFormData } from '@/utils/helpers';
import { mapState, mapGetters, mapActions } from 'vuex';
import vImageUploader from '@/components/image-uploader';
import {localizeField, multilingualFieldKeys, multilingualFields} from "@/helpers/locales";

const SectionStatuses = {
    DRAFT: 'draft',
    PUBLISH: 'publish'
};

const typeInitialState = () => ({
    name: null,
    ...multilingualFields(['name', 'description']),
    status: SectionStatuses.DRAFT,
    image: null,
    imgUrl: null
});

export default {
    name: 'EstablishmentsTypes',
    components: {
        vImageUploader,
    },
    data: () => ({
        isLoading: false,
        editDialog: false,
        removeDialog: false,
        idToRemove: null,
        editingLocale: 'uk',
        type: typeInitialState(),
        requestProcessing: false,
        filters: {
            query: null
        }
    }),
    setup() {
        return {
          localizeField
        };
    },
    computed: {
        ...mapGetters(['roleName']),
        ...mapState({
            items: state => state.establishmentsTypes.items,
            pagination: state => state.establishmentsTypes.pagination
        }),
        headers() {
            const titles = [...multilingualFieldKeys(['name', 'description']), 'status', 'createdAt', 'updatedAt'];
            // if (['admin', 'admin-delivery']) { //TODO: find out what it does
                titles.push('actions');
            // }
            return titles.map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.EstablishmentsTypes.table.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        statuses() {
            return Object.values(SectionStatuses).map(status => ({
                key: status,
                text: this.$vuetify.lang.t(`$vuetify.pages.EstablishmentsTypes.table.statuses.${status}`)
            }));
        },
        isEditMode() {
            return this.editDialog && this.type.id !== undefined;
        },
        isSaveBtnEnabled() {
            const requiredFields = ['status'];
            if (!this.isEditMode) {
                requiredFields.push('image');
            }
            return requiredFields.every((field) => !!this.type[field]) && multilingualFieldKeys(['name']).some((field) => !!this.type[field]);
        }
    },
    created() {
        this.loadItems(this.pagination.page);
    },
    methods: {
        ...mapActions({
            getItems: 'establishmentsTypes/getItems'
        }),
        loadItems(page = 1) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.getItems(Object.assign({}, {
                page,
                ...this.filters
            })).then(() => {
                this.$nextTick(() => this.requestProcessing = false);
            });
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.loadItems(page);
            }
        },
        onChooseImage(file) {
            this.type.image = file;
        },
        openEditDialog(item) {
            this.type = { ...this.type, ...(item || {}) };
            this.editDialog = true;
        },
        async saveType() {
            if (this.requestProcessing) {
                return;
            }

            if (this.type.image && this.type.image.size > 5*1000*1000) {
              return this.$store.dispatch('alert/addMessage', {
                message: `imageIsTooLarge`,
                time: 2000
              });
            }

            const { id } = this.type;

            const method = id ? 'put' : 'post';
            const url = id ? `/delivery/type/${id}` : '/delivery/type';

            const ignoredKeys = ['createdAt', 'updatedAt', 'imgUrl'];
            const preparedData = Object.reject(this.type, ignoredKeys);
            const data = getFormData(preparedData);

            this.requestProcessing = true;
            return request({ method, url, data})
                .then(() => {
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    this.closeEditDialog();
                    this.requestProcessing = false;
                    this.loadItems();
                })
                .catch(this.errorHandler)
                .finally(() => {
                    this.requestProcessing = false;
                });
        },
        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `brand.${message}`,
                    time: 2000
                });
            }
        },
        closeEditDialog() {
            this.resetData();
            this.editDialog = false;
        },
        openRemoveDialog(id) {
            this.idToRemove = id;
            this.removeDialog = true;
        },
        closeRemoveDialog() {
          this.idToRemove = null;
          this.removeDialog = false;
        },
        async remove() {
            if (this.requestProcessing) {
                return;
            }

            this.requestProcessing = true;
            return request({
                method: 'delete',
                url: `/delivery/type/${this.idToRemove}`
            }).then(res => {
                if (res.status === 200) {
                    this.requestProcessing = false;
                    this.closeRemoveDialog();
                    this.loadItems();
                }
            }).catch(error => this.errorHandler(error));
        },
        resetData() {
            this.type = typeInitialState();
        }
    }
};
</script>

<style lang="scss" scoped></style>
